import {HomeOutlined, PhoneOutlined, UpOutlined, BellOutlined} from "@ant-design/icons";
import {Button, Col, Modal, Row, Spin, Typography, Badge} from "antd";
import PropTypes from "prop-types";
import React, {useCallback, useContext, useEffect, useState} from "react";
import { useTranslation } from "react-i18next";
import {
    getDefinableContent,
    getDeploymentParamValue,
    getEntityRegisteredAddressObject,
    getEntityRegisteredNumber
} from "../../api/config";
import {
    FileSearchOutlined,
} from '@ant-design/icons';
import {useNavigate} from 'react-router-dom';
import { themeLocations } from "../../theme/themeLocations";
import {
    getRandomInt,
    isSwitchValueOn,
    getSessionData,
    storeLocalData,
    getLocalData,
} from "../../utility/global-util";
import { BannerImage } from "../image/BannerImage";
import { imageLocations } from "../image/ImageLocations";
import './Layout.css';
import {ReactComponent as CarCrash} from '../../assets/icons/car-crash.svg'
import {getDriverRouteById} from "../routes/driverRoutes";
import {useScreenSizeBelow} from "../../utility/hooks/useScreenSize";
import {getCscCompanyGroupConfig, getCscCustomerProfile} from "../../api/csc";
import GlobalContext from "../../GlobalContext";
import OurHTMLFormattedText from "../data/OurHTMLFormattedText";
import useOnlineStatus from "../../utility/hooks/useOnlineStatus";

const { Text } = Typography;


export const PoweredByBynx = () => {

    const { isOnline } = useOnlineStatus();
    const [showBynxLogo, setShowBynxLogo] = useState(false);

    useEffect(() => {
        if(isOnline) {
            // ONLINE
            storeLocalData('showBynxLogo', true); //unset prev set
            getDeploymentParamValue('ShowPowerByBynxOnPortals')
                .then(res => {
                    setShowBynxLogo(isSwitchValueOn(res));
                    storeLocalData('showBynxLogo', isSwitchValueOn(res));
                })
                .catch(ignoreErr => {
                    console.error(ignoreErr);
                    setShowBynxLogo(true);
                    storeLocalData('showBynxLogo', true);
                });
        } else {
            // OFFLINE
            const hideLogo = getLocalData('showBynxLogo') ? getLocalData('showBynxLogo') === 'false' : false;
            setShowBynxLogo(!hideLogo);
        }
    }, [setShowBynxLogo, isOnline]);

    return (
        showBynxLogo
        ? <Row align="middle">
            <Col>
                <Row justify="center">
                    <Text style={{ fontSize: '9px' }}>Powered by:</Text>
                </Row>
            </Col>
            <Col>
                <Row justify="start">
                    <BannerImage imageSrc={imageLocations.MAIN_BYNX_LOGO} height="30px" />
                </Row>
                <Row justify="center" style={{ marginTop: '-10px', marginLeft: '9px' }}>
                    <a href="https://www.bynx.com"><Text className="dp-powered-by-link">www.bynx.com</Text></a>
                </Row>
            </Col>
          </Row>
        : <></>
    );
}

const TabMenuButton = ({id, title, icon, route, action, disabled}) => {

    const globalData = useContext(GlobalContext);

    const navigate = useNavigate();

    const [tabSelected, setTabSelected] = useState(false);

    // Get current tab bar ID to see if this one should be selected
    useEffect(() => {
        setTabSelected(globalData.currentTabBarId ? globalData.currentTabBarId === id : false);
    }, [id, globalData.currentTabBarId]);

    // Click on tab bar item
    const onClick = () => {
        if (route) {
            console.log('..tab bar navigating to: ' + route);
            navigate(route);
        }
        else if (action) {
            action();
        }
    }

    return (
        <Col className="dp-footer-tab" onClick={disabled ? () => {} : onClick}>
           <Row justify="center" className={'dp-footer-tab-icon' + (disabled ? '-disabled' : (tabSelected ? '-selected' : ''))}>{icon}</Row>
           <Row justify="center"><Text className={'dp-footer-tab-text' + (disabled ? '-disabled' : (tabSelected ? '-selected' : ''))}>{title}</Text></Row>
        </Col>
    );
}

export const LinkOrPopup = ({title, definableContentCode, larger = false}) => {

    const { t } = useTranslation();
    const { isOnline } = useOnlineStatus();

    const [definableContent, setDefinableContent] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);

    const adjustPopupContent = useCallback((defContent) => {
        if (defContent != null && defContent.hasOwnProperty('content')) {
            let adjContent = defContent.content?.replaceAll('<li>', () => '<li key=' + getRandomInt(1, 10000) + '>');
            adjContent = adjContent?.replaceAll('<a', '<a target="_blank" rel="noreferrer"');  //always open links in new tab
            return adjContent;
        }
        return '';
    }, []);

    const isValueSet = useCallback((defContent) => {
        let hasVal = false;
        if (defContent.contentType === 'TXT') {
            hasVal = defContent.hasOwnProperty('content') && defContent.content != null;
        }
        else if (defContent.contentType === 'URL') {
            hasVal = defContent.hasOwnProperty('destinationUrl') && defContent.destinationUrl != null;
        }
        return hasVal;
    }, []);

    useEffect(() => {
        if(isOnline) {
            // ONLINE
            storeLocalData('defContent-' + definableContentCode, null); //unset prev set
            getDefinableContent(definableContentCode)
                .then(res => {
                    if (res != null) {
                        const defContent = {...res, content: adjustPopupContent(res), hasValue: isValueSet(res)};
                        setDefinableContent(defContent);
                        storeLocalData('defContent-' + definableContentCode, defContent);
                    } else {
                        setDefinableContent(null);
                    }
                })
                .catch(ignoreErr => {
                    console.log(ignoreErr);
                    setDefinableContent(null);
                });
        } else {
            // OFFLINE
            setDefinableContent(getLocalData('defContent-' + definableContentCode));
        }
    }, [setDefinableContent, definableContentCode, adjustPopupContent, isOnline, isValueSet]);

    const fixURL = (url) => {  //add http:// if no http:// or https:// given e.g. www. - anything else must be specified correctly in definable content
        if (typeof url === 'string' && url.indexOf('http://') === -1 && url.indexOf('https://') === -1) {
            return 'http://' + url;
        }
        return url;
    }

    const openContent = () => {
        setIsModalVisible(true);
    }

    return (
        <Col>
            {definableContent && definableContent.hasOwnProperty('hasValue') && definableContent.hasValue === true
                ? ( definableContent.contentType === 'TXT' && definableContent.content
                    ? <Text className="dp-footer-link" style={larger ? { fontSize: '12px' } : {}} onClick={() => openContent()}>{title}</Text>
                    : ( definableContent.contentType === 'URL' && definableContent.destinationUrl
                        ? <a href={fixURL(definableContent.destinationUrl)} target="_blank" rel="noreferrer"><Text className="dp-footer-link" style={larger ? { fontSize: '12px' } : {}}>{title}</Text></a>
                        : <Text classame="dp-footer-link-off" style={larger ? { fontSize: '12px' } : {}}>{title}</Text>
                      )
                  )
                : <Text className="dp-footer-link-off" style={larger ? { fontSize: '12px' } : {}}>{title}</Text>}
            <Modal className="std-portal-popup"
              open={isModalVisible}
              title={definableContent ? definableContent.title : title}
              closable={true}
              onCancel={() => setIsModalVisible(false)}
              keyboard={false}
              footer={[
                    <Button type="primary" onClick={() => setIsModalVisible(false)}>
                        {t('action.close')}
                    </Button>,
                ]}>
                <OurHTMLFormattedText htmlFormattedText={(definableContent ? definableContent.content : '')} />
            </Modal>
        </Col>
);
}

/**
 * The application footer.
 * @param props The react props.
 * @param showReturnToTop Show the 'return to top' icon or not.
 * @param showTabMenu Show teh tab bar or not (default to true, login sets to false - only shows at smaller screen sizes)
 */
const MainFooter = ({ showReturnToTop, showTabMenu = true, notifyToggle, notifyCount }) => {

    const { t } = useTranslation();

    const { isOnline } = useOnlineStatus();

    const eligibleForTabBar = useScreenSizeBelow(769);

    const [addressProgress, setAddressProgress] = useState(0);

    const [contactPhoneNumber, setContactPhoneNumber] = useState(null);
    const [customerPhoneNumber, setCustomerPhoneNumber] = useState(null);

    const scrollToTop = useCallback(() => {
        window.scrollTo(0, 0)
    }, [])

    const getRegNumber = useCallback((regNo) => {
        return regNo?.replaceAll('"', '');
    }, [])

    const getAddressItem = useCallback((item) => {
        if (item) {
            return item + ', ';
        }
        return '';
    }, []);

    const [permissions, setPermissions] = useState([]);

    const getFormattedRegAddress = useCallback((regAddress) => {
        if (regAddress != null && typeof regAddress === 'object') {
            let formatAddress = '';
            formatAddress = getAddressItem(regAddress.line1) + getAddressItem(regAddress.line2)
                            + getAddressItem(regAddress.line3) + getAddressItem(regAddress.townOrCity)
                            + getAddressItem(regAddress.region) + getAddressItem(regAddress.postcode)
                            + getAddressItem(regAddress.countryDesc);
            formatAddress = formatAddress.substring(0, formatAddress.length - 2);
            return formatAddress;
        }
        return '';
    }, [getAddressItem])

    const [entityRegAddress, setEntityRegAddress] = useState(null);
    const [entityRegNumber, setEntityRegNumber] = useState('');

    // Phone Numbers Control
    const getPhoneNumberToPhone = () => {
        return customerPhoneNumber != null ? customerPhoneNumber : contactPhoneNumber;
    }
    const isThereAPhoneNumber = () => {
        return getPhoneNumberToPhone() != null;
    }
    const phoneTheNumber = () => {
        console.log('..calling customer support no: ' + getPhoneNumberToPhone());
        window.open('tel:' + getPhoneNumberToPhone(), '_self');
    }

    const larger = false;

    // VARIOUS LOADS
    useEffect(() => {
        if(isOnline) {
            // ONLINE
            storeLocalData('entityRegNumber', null); //unset prev set
            getEntityRegisteredNumber()
                .then(res => {
                    const regNo = getRegNumber(res);
                    setEntityRegNumber(regNo);
                    storeLocalData('entityRegNumber', regNo);
                })
                .catch(ignoreErr => {
                    console.log(ignoreErr);
                    setEntityRegNumber('');
                });
        } else {
            // OFFLINE
            setEntityRegNumber(getLocalData('entityRegNumber'));
            //progress on address next
        }
    }, [setEntityRegNumber, getRegNumber, isOnline]);

    useEffect(() => {
        if(isOnline) {
            // ONLINE
            storeLocalData('entityRegAddress', null); //unset prev set
            getEntityRegisteredAddressObject(setAddressProgress)
                .then(res => {
                    setEntityRegAddress(res);
                    storeLocalData('entityRegAddress', res);
                })
                .catch(ignoreErr => {
                    console.log(ignoreErr);
                    setEntityRegAddress(null);
                });
        } else {
            // OFFLINE
            setEntityRegAddress(getLocalData('entityRegAddress'));
            setAddressProgress(100);
        }
    }, [setEntityRegAddress, isOnline]);

    useEffect(() => setPermissions(getSessionData('driverPermissions')), [setPermissions]);  //online and offline

    useEffect(() => {
        if(isOnline) {
            // ONLINE
            storeLocalData('coGroupContactNo2', null); //unset prev set
            storeLocalData('customerContactNo2', null);

            getCscCompanyGroupConfig() //no progress in this case (not visible)
                .then(res => {
                    if (res && res.contactPhoneNumber) {
                        setContactPhoneNumber(res.contactPhoneNumber);
                        storeLocalData('coGroupContactNo2', res.contactPhoneNumber);
                    }
                })
                .catch(ignoreErr => {
                    console.log(ignoreErr);
                    setContactPhoneNumber(null);
                });

            const userCustomer = getSessionData('selectedCustomer');
            let userCustomerAccount = '';
            let userCustomerEntity = '';
            if (userCustomer) {
                userCustomerAccount = userCustomer.code ? userCustomer.code : '';
                userCustomerEntity = userCustomer.entityId ? userCustomer.entityId : '';
                if (userCustomerEntity && userCustomerAccount) {
                    getCscCustomerProfile(userCustomerEntity, 'C', userCustomerAccount) //no progress in this case (not visible)
                        .then(res => {
                            if (res && res.phoneNumber) {
                                setCustomerPhoneNumber(res.phoneNumber);
                                storeLocalData('customerContactNo2', res.phoneNumber);
                            }
                        })
                        .catch(ignoreErr => {
                            console.log(ignoreErr);
                        });
                }
            }
        } else {
            // OFFLINE
            setContactPhoneNumber(getLocalData('coGroupContactNo2'));
            setCustomerPhoneNumber(getLocalData('customerContactNo2'));
        }

    }, [isOnline]);

    // Handle no allocated contracts
    const [dummyMode, setDummyMode] = useState(false);
    const allocContracts = getSessionData("driverHasAllocContracts"); //comes back as string
    useEffect(() => {
        setDummyMode(allocContracts ? allocContracts === 'false': false);
    }, [allocContracts, setDummyMode]);

    // RENDER
    return (
        <footer className={'dp-footer' + (showTabMenu && eligibleForTabBar ? ' dp-footer-with-tab' : '')} style={{ zIndex: '999' }}>
            { showTabMenu && eligibleForTabBar
              ? <div className="dp-tab-bar">
                    <div className="dp-tab-bar-banner">
                        {/*//Do not currently show scroll to top in app/tab bar mode*/}
                        {/*{ showReturnToTop*/}
                        {/*    ? <UpOutlined className="dp-footer-return-top" style={{ marginTop: '-38px' }} onClick={() => scrollToTop()} />*/}
                        {/*    : <></> }*/}
                        <Row className="dp-tab-bar-buttons" align="top" justify="space-between">

                            <TabMenuButton id="home" icon={<HomeOutlined />} title={t("title.home")}  route={getDriverRouteById('home')} />

                            {!dummyMode && permissions.includes("DRIVERS_PORTAL_INCIDENT_MANAGEMENT") ? (
                                <TabMenuButton id="incident" icon={<CarCrash style={{height:"1em"}} className="anticon"/>} title={t("action.new_incident")} route={getDriverRouteById('record-incident')} disabled={!isOnline} />
                            ): (<></>)}

                            {!dummyMode && permissions.includes("DRIVERS_PORTAL_CORPORATE_CUSTOMER_INSPECTION") ? (
                                <TabMenuButton id="inspect" icon={<FileSearchOutlined />} title={t("action.inspect")} route={getDriverRouteById('walkaround')} disabled={!isOnline} />
                            ): (<></>)}

                            <TabMenuButton id="notify" icon={<Badge count={notifyCount} style={{ marginTop: '6px' }}><BellOutlined /></Badge>} title={t('title.notifications')} action={() => notifyToggle()} />

                            <TabMenuButton id="phone" icon={<PhoneOutlined />} title={t("action.contact_us")} action={phoneTheNumber} disabled={!isThereAPhoneNumber()} />

                            {/*** NOTE *** Can fit 4 on screens from 320 and up - TODO asses options for 5, stop 3rd line wrapping for translations */}
                        </Row>
                    </div>
                </div>
              : <div>
                    <div className="dp-footer-banner">
                        { showReturnToTop
                          ? <UpOutlined className="dp-footer-return-top" onClick={() => scrollToTop()} />
                          : <></> }
                    </div>
                    {/*Similar to Information Page*/}
                    <div className="dp-footer-centre">
                        <Row justify="space-between" className="dp-footer-top-row">
                            <Col>
                                <BannerImage imageSrc={themeLocations.MAIN_LOGO} height="30px" />
                            </Col>
                            <Col>
                                <></>
                            </Col>
                        </Row>
                        <Row justify="space-between" className="dp-footer-bottom-row">
                            <Col>
                                <Row>
                                    { addressProgress < 100
                                      ? <Spin size="small" />
                                      : <Text style={larger ?{ fontSize: '9px' } : {}}>{getFormattedRegAddress(entityRegAddress)}</Text>
                                    }
                                </Row>
                                <Row>
                                    <Text style={larger ? { fontSize: '9px' } : {}}>{t('field.co_reg_no') + ': ' + (entityRegNumber ? entityRegNumber : '')}</Text>
                                </Row>
                                <Row align="middle">
                                    <LinkOrPopup larger={larger} title={t('title.legal')} definableContentCode="EXTERNAL_LEGAL" />
                                    <LinkOrPopup larger={larger} title={t('field.terms_conditions')} definableContentCode="EXTERNAL_TERMS" />
                                    <LinkOrPopup larger={larger} title={t('title.privacy')} definableContentCode="EXTERNAL_PRIVACY" />
                                    <LinkOrPopup larger={larger} title={t('title.cookies')} definableContentCode="EXTERNAL_COOKIES" />
                                    <LinkOrPopup larger={larger} title={t('title.accessibility')} definableContentCode="EXTERNAL_ACCESSIBILITY" />
                                </Row>
                                <Text style={larger ? { fontSize: '12px' } : {}}>&copy; {t('field.app_version') + ': ' + getSessionData('APP_VERSION')}</Text>
                            </Col>
                            <Col style={{ paddingTop: '15px' }}>
                                <PoweredByBynx />
                            </Col>
                        </Row>
                    </div>
                </div> }
        </footer>
    );
}

// Validate prop type parameters.
MainFooter.propTypes = {
    showReturnToTop: PropTypes.bool,
};

MainFooter.defaultProps = {
    showReturnToTop: false,
};

export default MainFooter;